import { trapFocus, removeTrapFocus } from '@theme/focus';

/**
 * `<rp-mobile-menu>` — the behaviour layer for the mobile navigation panel
 * (spec 005), around the `<details>` in `blocks/repose-mobile-menu.liquid`.
 *
 * ── WHY THIS EXISTS INSTEAD OF `assets/header-drawer.js` ────────────────────
 *
 * Two shipped attempts at this component failed on the same mechanism, in
 * opposite directions, and both failures come from ONE property of Horizon's
 * `header-drawer.js`: it writes the `open` attribute from a DEFERRED callback.
 *
 *     header-drawer.js:124   onAnimationEnd(drawer || details, () => { reset(details); … })
 *     header-drawer.js:186   element.removeAttribute('open')      // inside reset()
 *     utilities.js:357       return Promise.allSettled(animationPromises).then(callback)
 *
 * A `<summary>` click has a second writer of `open`: the element's own native
 * activation behaviour. So every close is a race between the browser's write and
 * a JavaScript write whose timing is set by whatever animation happened to be
 * running on `.menu-drawer`.
 *
 *   • Nothing running  → `Promise.allSettled([])` resolves at the next microtask
 *     checkpoint, which arrives while the click is still dispatching. `reset()`
 *     removes `open`, native activation then adds it back, and the menu cannot be
 *     closed at all. (Attempt 1.)
 *   • A CSS animation running → its `finished` promise does not settle while the
 *     details content is unrendered. It settles on the NEXT activation, so that
 *     activation's `open` is removed a beat after the browser set it, and every
 *     third tap does nothing. (Attempt 2.)
 *
 * MEASURED, 8 real CDP touch taps at a 1.5s cadence, 375x812, classified from
 * screenshot bytes alone:
 *
 *     with the exit animation rule       TFFTFFTF   (taps 3 and 6 byte-identical
 *                                                    to the frame before them)
 *     that ONE rule deleted at runtime   TTTTTTTT   (attempt 1's defect returns)
 *
 * The rule is simultaneously the fix for the first defect and the cause of the
 * second, which is the signature of a race that has been retimed rather than
 * removed.
 *
 * ── WHAT THIS MODULE DOES DIFFERENTLY ───────────────────────────────────────
 *
 * It never writes `open` on the activation path. The native `<details>` is the
 * only writer, so one tap is exactly one state change and there is no second
 * writer to race. This element only REACTS, from the native `toggle` event, which
 * the browser fires AFTER `open` has already changed:
 *
 *   • focus trap on / off
 *   • `aria-expanded` on the summary, mirroring state that has already settled
 *
 * It writes state in exactly two places, Escape and the 990px breakpoint below.
 * Neither is an activation — no native toggle is in flight in either — and both
 * write synchronously, never behind a promise.
 *
 * Everything else the drawer needed is already someone else's job and is
 * untouched by this change:
 *   • background scroll lock — `assets/dialog.js:175-190` listens for `toggle` in
 *     the capture phase on any `<details scroll-lock>` and sets `html[scroll-lock]`.
 *     It never involved `header-drawer.js`.
 *   • the open/close animation — `src/u6-mobile.css`, driven entirely by `[open]`
 *     and `::details-content`. No JavaScript participates, so no promise can
 *     leave a write pending.
 *
 * ── ONE THING IT DOES OWN: THE 990px BREAKPOINT ─────────────────────────────
 *
 * 🔴 SHIPPED DEFECT, recorded in docs/BUILD.md since the U-MM review and fixed
 * here: *"open the menu at 375px, widen past 990px, and the page stays
 * scroll-locked with no control to release it."*
 *
 * The panel is `display: none` above 990px, but `display` does not close a
 * `<details>`. The element stays `[open]`, so:
 *
 *   • `assets/dialog.js:175` never sees a `toggle`, never calls `unlockScroll`,
 *     and `html[scroll-lock]` stands — which `assets/base.css:36` turns into
 *     `overflow: hidden` on BOTH the root and `.page-wrapper`. Above 990px
 *     `.page-wrapper` is the page scroller, so the whole page is frozen with no
 *     visible control left to release it: the summary that would toggle it is
 *     the thing that was just hidden.
 *   • `trapFocus(details)` is still installed on a subtree nobody can see.
 *
 * So the breakpoint closes it. A `matchMedia` change is NOT an activation —
 * no native `toggle` is in flight — so this write is safe for exactly the
 * reason the Escape write is, and it is made the same way: synchronously, never
 * behind a promise. Closing fires `toggle`, which releases the scroll lock and
 * the focus trap through the paths that already exist; nothing here duplicates
 * either of them.
 *
 * `matchMedia`, never `resize`: `docs/specs/060-motion-system.md` §6.3 caps
 * `window` resize listeners at 0 and docs/BUILD.md counts the five inherited
 * ones. This adds none.
 *
 * 990px is `assets/base.css:28`'s own breakpoint and `src/app.css`'s — the
 * width at which `.site-header__burger` becomes `display: none` and
 * `.page-wrapper` becomes the scroller. It is written here as a literal for the
 * same reason `header-drawer.js` did: a media query cannot read a custom
 * property. If that number ever moves, it moves in three files.
 */
class RpMobileMenu extends HTMLElement {
  /** @type {HTMLDetailsElement | null} */
  #details = null;

  /** The width at which the panel stops being rendered. See the header note. */
  #desktop = matchMedia('(min-width: 990px)');

  connectedCallback() {
    this.#details = this.querySelector('details');
    if (!this.#details) return;

    // `toggle` is the native event, fired after `open` has already changed. A
    // listener on it can only ever describe state, never contend for it.
    this.#details.addEventListener('toggle', this.#onToggle);
    this.addEventListener('keydown', this.#onKeyDown);
    this.#desktop.addEventListener('change', this.#onBreakpoint);
    this.#onToggle();
    // The panel can also be open at connect time on a desktop-width first
    // paint — a `<details open>` restored by bfcache, or a theme-editor
    // re-render. Same close, same reason.
    this.#onBreakpoint();
  }

  disconnectedCallback() {
    this.#details?.removeEventListener('toggle', this.#onToggle);
    this.removeEventListener('keydown', this.#onKeyDown);
    this.#desktop.removeEventListener('change', this.#onBreakpoint);
    removeTrapFocus();
  }

  #onBreakpoint = () => {
    const details = this.#details;
    if (!details?.open || !this.#desktop.matches) return;

    // Focus first: the subtree is about to stop being rendered, and unlike the
    // Escape path there is nowhere inside it left to land — the summary is
    // `display: none` above this breakpoint. Blur hands focus back to the
    // document, which is the only honest answer when the control a keyboard
    // user was on has just ceased to exist.
    const active = document.activeElement;
    if (active instanceof HTMLElement && details.contains(active)) active.blur();

    // The second and last `open` write in this file, and synchronous for the
    // same reason as the first. `toggle` fires from it, which is what releases
    // `html[scroll-lock]` and the focus trap.
    details.open = false;
  };

  #onToggle = () => {
    const details = this.#details;
    if (!details) return;

    const summary = details.querySelector('summary');
    // Rendered with NO `aria-expanded` so the value is never wrong while
    // scripting is unavailable — a `<summary>` already exposes expanded state
    // implicitly, and a stale explicit attribute would override it. Once this
    // module is running the attribute is written from state that has already
    // settled, which is the only way it can be trusted.
    summary?.setAttribute('aria-expanded', String(details.open));

    if (details.open) {
      // The first focusable inside the details IS the summary, so the trap's
      // wrap-around lands on the control that now reads "Close" — 005 §5's
      // "the escape route is the first thing announced", without an explicit
      // focus move that would fight the pointer.
      trapFocus(details);
    } else {
      removeTrapFocus();
    }
  };

  /** @param {KeyboardEvent} event */
  #onKeyDown = (event) => {
    if (event.key !== 'Escape') return;

    const details = this.#details;
    if (!details?.open) return;

    event.preventDefault();
    // Synchronous, and the only `open` write in this file. Escape is not an
    // activation, so nothing else is writing `open` in this task.
    details.open = false;
    // Focus is inside a subtree that is about to stop being rendered, so it has
    // to be moved deliberately; the summary is where a keyboard user was when
    // they opened the panel.
    details.querySelector('summary')?.focus();
  };
}

if (!customElements.get('rp-mobile-menu')) {
  customElements.define('rp-mobile-menu', RpMobileMenu);
}
